import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import { Calendar, ChevronRight } from "lucide-react";
import { useQuote } from "./QuoteContext";
import CompareContractsModal from "./CompareContractsModal";

const SLIDES = [
  {
    desktop: "/images/hero-puy-mary-cantal.webp",
    mobile: "/images/hero-puy-mary-cantal-mobile.webp",
    altDesktop: "puy-mary-cantal-courtier-assurance-optina",
    altMobile: "puy-mary-cantal-lever-soleil-optina",
  },
  {
    desktop: "/images/hero-viaduc-garabit-cantal.webp",
    mobile: "/images/hero-viaduc-garabit-cantal-mobile.webp",
    altDesktop: "viaduc-de-garabit-cantal-courtier-assurance",
    altMobile: "viaduc-garabit-cantal-courtier",
  },
  {
    desktop: "/images/mutuelle-sante-professionnelle-senior.webp",
    mobile: "/images/mutuelle-sante-professionnelle-senior.webp",
    altDesktop: "courtier-mutuelle-sante-professionnelle-senior",
    altMobile: "courtier-mutuelle-sante-aurillac-paris",
  },
  {
    desktop: "/images/assurance-auto-comparateur-paris.webp",
    mobile: "/images/assurance-auto-pas-cher-aurillac-mobile.webp",
    altDesktop: "assurance-auto-courtier-independant",
    altMobile: "assurance-auto-aurillac-paris",
  },
];

const SLIDE_DURATION_MS = 4500;

/**
 * DynamicWord — fade-in / fade-out lent d'un seul mot, esprit Hermès / Kretz.
 * Aucun effet typewriter, aucun curseur clignotant, même couleur que le texte parent.
 */
function DynamicWord({ words, intervalMs = 2800 }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % words.length);
    }, intervalMs);
    return () => clearInterval(id);
  }, [words.length, intervalMs]);

  return (
    <span
      data-testid="dynamic-word"
      className="relative inline-flex align-baseline overflow-visible"
      style={{ minWidth: "16ch" }}
    >
      {/* Mot invisible servant à fixer la largeur (pour éviter tout saut) */}
      <span aria-hidden="true" className="invisible whitespace-nowrap">
        {words[index]}
      </span>
      <AnimatePresence initial={false}>
        <motion.span
          key={words[index]}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.7, ease: "easeInOut" }}
          className="absolute left-0 top-0 inline-block whitespace-nowrap"
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 80]);
  const { openQuote } = useQuote();
  const [index, setIndex] = useState(0);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    SLIDES.forEach((s) => {
      const a = new Image();
      a.src = s.desktop;
      const b = new Image();
      b.src = s.mobile;
    });
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % SLIDES.length), SLIDE_DURATION_MS);
    return () => clearInterval(t);
  }, []);

  const current = SLIDES[index];

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[calc(90vh-80px)] md:min-h-[calc(100vh-88px)] flex items-end overflow-hidden bg-brand-sand-2 mt-20 md:mt-[88px]"
    >
      <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
        <AnimatePresence mode="sync">
          <motion.div
            key={current.desktop}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
          >
            <motion.div
              initial={{ scale: 1.0 }}
              animate={{ scale: 1.08 }}
              transition={{ duration: SLIDE_DURATION_MS / 1000, ease: "linear" }}
              className="absolute inset-0"
            >
              <picture>
                <source media="(min-width: 768px)" srcSet={current.desktop} />
                <img
                  src={current.mobile}
                  alt={current.altMobile}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={index === 0 ? "high" : "auto"}
                  data-testid={`hero-slide-${index}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </picture>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {/* Overlay luxe — voile noir 40-45 % en haut, 55-60 % en bas pour
          lisibilité parfaite du texte blanc sur la photo du Puy Mary. */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(to bottom, rgba(0,0,0,0.42) 0%, rgba(0,0,0,0.48) 45%, rgba(0,0,0,0.60) 100%)",
        }}
        data-testid="hero-overlay"
      />

      <CompareContractsModal open={compareOpen} onClose={() => setCompareOpen(false)} />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-20 pb-14 md:pb-20 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.2, 0.7, 0.2, 1] }}
          className="max-w-3xl mx-auto text-center md:mx-0 md:text-left md:max-w-4xl"
        >
          {/* H1 directement sur l'image, sans encadré ni backdrop.
              Mobile : légèrement plus grand que la version précédente. */}
          <h1
            data-testid="hero-title"
            className="font-serif text-white text-6xl sm:text-7xl md:text-7xl lg:text-[92px] leading-[0.98] tracking-tight font-medium drop-shadow-[0_3px_14px_rgba(0,0,0,0.55)]"
          >
            Je compare.
            <br />
            <span className="italic font-editorial text-white">Vous économisez.</span>
          </h1>

          {/* Sous-titre : posture courtier indépendant, casse premium. */}
          <div
            data-testid="hero-luxe-tagline"
            className="mt-8 md:mt-10 mx-auto md:mx-0 max-w-[620px] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.55)]"
          >
            <p className="text-[18px] md:text-[20px] font-medium leading-snug">
              Courtier indépendant en{" "}
              <span className="whitespace-nowrap">
                Assurance{" "}
                <span className="font-editorial italic font-normal text-[1.1em]">&amp;</span> Énergie
              </span>
              .
            </p>
            <p className="mt-4 text-[15px] md:text-[17px] font-light leading-relaxed text-white/90">
              J'analyse, je négocie et j'optimise vos contrats pour alléger vos charges — un seul
              interlocuteur, à vos côtés.
            </p>

            {/* Mot dynamique — agrandi, fade lent, même blanc que le texte */}
            <p
              className="mt-7 md:mt-8 font-serif italic text-[22px] md:text-[28px] tracking-tight text-white leading-tight drop-shadow-[0_2px_12px_rgba(0,0,0,0.55)]"
              data-testid="hero-dynamic-line"
            >
              J'optimise vos&nbsp;
              <DynamicWord
                words={[
                  "assurances",
                  "factures d'énergie",
                  "contrats pro",
                  "assurance de prêt",
                ]}
              />
              .
            </p>
          </div>

          <div className="mt-9 md:mt-10 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center md:justify-start gap-3 md:gap-4">
            <Link
              to="/rendez-vous"
              data-testid="hero-quote-btn"
              className="btn-press inline-flex items-center justify-center gap-2 rounded-full bg-white hover:bg-white/90 text-[#1A1A1A] px-7 py-4 text-[12px] md:text-sm font-semibold tracking-[0.18em] uppercase shadow-[0_18px_36px_rgba(0,0,0,0.35)] min-h-[52px]"
            >
              <Calendar className="h-4 w-4" /> Mon devis en − de 24h
            </Link>
            <button
              type="button"
              data-testid="hero-compare-btn"
              onClick={() => {
                if (window.innerWidth >= 1024) {
                  setCompareOpen(true);
                } else {
                  openQuote();
                }
              }}
              className="btn-press inline-flex items-center justify-center gap-2 rounded-full border border-white hover:bg-white hover:text-[#1A1A1A] text-white px-7 py-4 text-[12px] md:text-sm font-semibold tracking-[0.18em] uppercase transition-colors min-h-[52px]"
            >
              Découvrir mes services <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </motion.div>

        {/* Indicateurs du slider — les piliers sont déplacés hors du Hero
            (dans <PillarsBand /> juste après, sur fond clair). */}
        <div className="mt-12 md:mt-16 flex items-center justify-center md:justify-start gap-2" data-testid="hero-indicators">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              data-testid={`hero-indicator-${i}`}
              onClick={() => setIndex(i)}
              aria-label={`Photo ${i + 1}`}
              className={`h-[2px] transition-all duration-700 ${
                i === index ? "w-12 bg-white" : "w-6 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
